import { useCallback } from 'react'
import { useCreateTask } from '~/api/supabase-mutations'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCurrentUser } from '~/common/use-current-user'
import { taskAdded, useCropsContextDispatch } from '~/common/context/crops/crops-context'
import { useOrgParams } from '~/common/use-org-params'

export const useCreateNewTask = ({
  taskCode,
  orgId,
  cropId = null,
  startDate,
  durationDays,
  notes,
}) => {
  const { currentUser } = useCurrentUser()
  const { createTask, loading, error } = useCreateTask()
  const cropsDispatch = useCropsContextDispatch()
  const { seasonId } = useOrgParams()

  const submitTask = useCallback(async ({ onSuccess, onError } = {}) => {
    if (!taskCode || !orgId || !startDate || !currentUser) return

    const taskData = {
      task_code: taskCode,
      organization_id: orgId,
      crop_id: cropId ?? null,
      season_id: seasonId ?? null,
      state: 'ready',
      task_window_start: startDate.toISOString(),
      duration: `${durationDays} days`,
      notes: notes || null,
      created_by: currentUser.id,
    }

    const { data: task, error: taskError } = await createTask(taskData)

    if (taskError) {
      console.error('Failed creating task record', taskError)
      onError?.(taskError)
      return
    }

    // Invalidate caches and notify listeners to refetch
    // Crops, schedules, phases, and tasks are used by crop lists and timeline views
    supabaseGetter.crops.invalidate()
    supabaseGetter.cropSchedules.invalidate()
    supabaseGetter.cropPhases.invalidate()
    supabaseGetter.cropDetails.invalidate()
    supabaseGetter.tasks.invalidate()
    cropsDispatch(taskAdded({ taskId: task.id }))

    onSuccess?.(task)
  }, [taskCode, orgId, cropId, seasonId, startDate, durationDays, notes, currentUser, createTask, cropsDispatch])

  return { submitTask, loading, error }
}
